import {observable, makeObservable, action, computed, toJS} from 'mobx';
import {map, max, forEach, values, keyBy, uniq, flatten, filter, transform, range, compact, groupBy, size, sortBy, min,
  every, omit, without, countBy, set, find, first, isNil, isNull, isEmpty, Dictionary, get,
  assign, isNumber,
  reject} from 'lodash';
import {Message} from 'semantic-ui-react';

import {generateLocalId} from '../../cablingMapEditor/utils';
import Positioner from '../../cablingMapEditor/Positioner';

import {ctrlEntityTypes, ctrlNodeWidth, ctrlGridStep, ctrlNodeHeight} from '../../cablingMapEditor/const';
import {ZONES_ROLE_ORDER, NODE_ROLES, REDUNDANCY, PEER_LINKS_TYPES, NO_REDUNDANCY, DESIGN, PORT_ROLES, ROLES_NODE_TYPES,
  RAIL_ZONE, LAG_MODES, DEFAULT_ZONES} from '../const';
import {makeSafeName, pickPortChannelFromRange, randomPrefix, speedFromString} from '../utils';
import {request} from '../../exports';

import Port from './Port';
import Node from './Node';
import Zone from './Zone';
import LinksGroup from './LinksGroup';
import Step from '../../cablingMapEditor/store/Step';
import ChangesStore, {Change} from '../../cablingMapEditor/store/ChangesStore';
import SelectionStore from '../../cablingMapEditor/store/SelectionStore';
import {AccessNodeDataType, LeafNodeDataType, LogicalDeviceType, NodeDataType, NodePositionType, PeerLinksTypesType,
  SwitchesByNameType} from '../types';

const entities = {
  [ctrlEntityTypes.NODE]: Node,
  [ctrlEntityTypes.LINKS_GROUP]: LinksGroup
};

const collections = {
  [ctrlEntityTypes.NODE]: 'nodes',
  [ctrlEntityTypes.LINKS_GROUP]: 'linksGroups'
};

const noLinks = {
  count: 0,
  speed: null
};

type portChannelIdCollectorType<T> = (acc: T, portChannelIds: number[], genericName?: string) => T;

class RackStore {
  rootStore;

  @observable changes: ChangesStore;

  @observable nodes: Dictionary<Node> = {};
  @observable linksGroups: Dictionary<LinksGroup> = {};

  @observable id: string | null = null;
  @observable name = '';
  @observable description = '';

  @observable design = DESIGN.L3CLOS;

  @observable editedNode: Node | null = null;
  @observable optionsAreShown = false;

  @observable positioner = new Positioner([]);

  @observable zones: Record<string, Zone> = {};
  @observable devices: Dictionary<LogicalDeviceType> = {};

  @observable openedLinksGroupId: string | undefined;
  @observable isSaving = false;

  @observable supportsRails = false;

  @observable portChannelsAreManaged: Node | null = null;

  @computed
  get hasDraftLinks() {
    return !isEmpty(this.draftLinksGroups);
  }

  @action
  commitDraftLinks() {
    // Open the first draft links group after commit
    if (size(this.draftLinksGroups) === 1) {
      this.openedLinksGroupId = first<LinksGroup>(this.draftLinksGroups)?.id;
    }

    // Marking all draft links as non-draft
    forEach(this.draftLinksGroups, (draftLinkGroup) => {
      draftLinkGroup.isDraft = false;
    });

    // And then updating the store
    this.linksGroups = {...this.linksGroups};
  }

  @action
  removeDraftLinks() {
    this.linksGroups = omit(this.linksGroups, map(this.draftLinksGroups, 'id'));
  }

  _tags: string[] = [];

  @observable error = null;

  @computed
  get isNew() {
    return !this.id;
  }

  @computed.struct
  get linksGroupsSorted() {
    return sortBy(this.linksGroups, 'id');
  }

  @computed.struct
  get draftLinksGroups(): LinksGroup[] {
    return filter<LinksGroup>(values(this.linksGroups), {isDraft: true});
  }

  @computed
  get isLinkerOpened() {
    return !!this.openedLinksGroupId || this.hasDraftLinks;
  }

  @computed
  get availableTags() {
    return compact(uniq([
      ...this._tags,
      ...this.usedTags
    ]));
  }

  @computed
  get usedTags() {
    return compact(uniq([
      ...flatten(map(this.nodes, 'tags')),
      ...flatten(map(this.linksGroups, 'tags'))
    ]));
  }

  @computed
  get usedRailIndices() {
    return without(uniq(map(values(this.linksGroups), 'railIndex')), null, undefined).sort();
  }

  @computed
  get availableRailIndex() {
    let index = 0;
    while (this.usedRailIndices.includes(index)) index++;
    return index;
  }

  @computed
  get railsLinksByLeafId(): Dictionary<LinksGroup[]> {
    return groupBy(filter(this.linksGroups, 'isInRail'), 'toNode.id');
  }

  @computed
  get railsSpeedsByGenericId(): Dictionary<string> {
    return transform(
      filter(this.linksGroups, {isInRail: true, isDraft: false}),
      (acc, {fromNode, speedString}) => {
        acc[fromNode!.id] = speedString;
      },
      {}
    );
  }

  @computed
  get railsZone() {
    return find(this.zones, {role: RAIL_ZONE});
  }

  @computed
  get isL3Clos() {
    return this.design === DESIGN.L3CLOS;
  }

  @computed
  get isL3Collapsed() {
    return this.design === DESIGN.L3COLLAPSED;
  }

  @computed
  get canBecomeRailCollapsed() {
    // If has rail links OR ...
    return this.hasRails || (
      // has no access switches AND ...
      !this.accessSwitchNodesCount &&
      // no leaf pairs AND
      !this.hasLeafPairs &&
      // no LAG links
      !this.lagLinksCount
    );
  }

  @computed
  get leafsWithRails() {
    return transform(
      this.linksGroupsSorted,
      (acc, {toNode, isInRail}) => {
        if (isInRail && toNode?.isLeaf) {
          acc[toNode.id] = true;
          if (toNode.isPaired) acc[toNode.pairedWith!.id] = true;
        }
      },
      {}
    );
  }

  @computed
  get isRailCollapsed() {
    return this.design === DESIGN.RAILCOLLAPSED;
  }

  @computed
  get hasRails() {
    // Only rail collapsed rack can have rail links
    return size(this.railsLinks) > 0;
  }

  getSwitchesPortChannelIds<T = number[]>(processorFn: portChannelIdCollectorType<T>): Dictionary<T> {
    return transform(
      // Filtering out generics, iterating through all switch nodes
      reject(this.nodes, 'isGeneric'),
      (pcIds, switchNode) => {
        const {isPaired, isFirstInPair, portChannelId: pcL2, portChannelIdExt: pcL3, name} = switchNode;

        // Only take first nodes in pairs or not paired switches
        if (isPaired && !isFirstInPair) return;

        const seenNames = {};

        // Iterate through all their links (peer and to generics)
        forEach(
          switchNode.myLinksGroups,
          (linksGroup) => {
            const {count, isPeer, isL3Peer} = linksGroup;
            // Empty links does not affect counts
            if (!count) return;

            if (isPeer) {
              // Take into account peer links port channel Ids
              const pcId = isL3Peer ? pcL3 : pcL2;
              if (pcId) {
                pcIds[name] = processorFn(pcIds?.[name], [pcId], name);
              }
            } else {
              // If link is to generic - count its port channel Id in
              const {name: genericName, isGeneric, myPortChannelIds} = linksGroup.oppositeToNode(switchNode)!;
              if (isGeneric && !seenNames[genericName]) {
                pcIds[name] = processorFn(pcIds?.[name], myPortChannelIds, genericName);
                seenNames[genericName] = true;
              }
            }
          }
        );
      },
      {}
    );
  }

  @computed
  get switchPortChannelIds(): Dictionary<number[]> {
    return this.getSwitchesPortChannelIds(
      (acc: number[], portChannelIds: number[]): number[] => [...(acc ?? []), ...portChannelIds]
    );
  }

  // Serves to find duplicated port channels IDs on generic nodes
  @computed
  get portChannelsDuplicates(): Dictionary<number[]> {
    // Group port channels by switch nodes
    const nodePortChannelsPerSwitch = this.getSwitchesPortChannelIds(
      (acc: Dictionary<string[]> = {}, portChannelIds, genericName) => {
        forEach(portChannelIds, (portChannelId) => {
          (acc[portChannelId] ??= []).push(genericName as string);
        });
        return acc;
      }
    );

    // Unwrap into generic port channels
    const genericPortChannelsDuplicates: Dictionary<number[]> = transform(
      nodePortChannelsPerSwitch,
      (acc, switchPortChannelIds) => {
        // Check if some of PCIDs are duplicated
        forEach(switchPortChannelIds, (names, portChannelId) => {
          if (size(names) > 1) {
            // and then add them to the resulting collection
            forEach(names, (name) => {
              (acc[name] ??= []).push(+portChannelId);
            });
          }
        });
      },
      {}
    );

    // Remove duplicated IDs for each of nodes
    return transform(
      genericPortChannelsDuplicates,
      (acc, pcIds, nodeName) => {
        acc[nodeName] = uniq(pcIds);
      },
      {}
    );
  }

  @computed
  get usedLabels() {
    return transform(
      this.nodes,
      (acc, {_label, label, isPaired, isFirstInPair}) => {
        if (isPaired) {
          set(acc, label, (acc?.[label] ?? 0) + 1);
          // Avoiding duplication of _label for paired nodes
          if (!isFirstInPair) return;
        }
        set(acc, _label, (acc?.[_label] ?? 0) + 1);
      },
      {}
    );
  }

  @computed.struct
  get leafNodes() {
    return sortBy(filter(this.nodes, 'isLeaf'), 'id');
  }

  @computed
  get accessSwitchNodesCount() {
    return size(filter(this.nodes, 'isAccessSwitch'));
  }

  @computed
  get leafPairsCount() {
    return countBy(filter(this.leafNodes, 'isPaired'), 'redundancyProtocol');
  }

  @computed
  get hasLeafPairs() {
    return this.mlagLeafPairsCount > 0 || this.esiLeafPairsCount > 0;
  }

  @computed
  get mlagLeafPairsCount() {
    return (this.leafPairsCount?.[REDUNDANCY.MLAG] || 0) / 2;
  }

  @computed
  get esiLeafPairsCount() {
    return (this.leafPairsCount?.[REDUNDANCY.ESI] || 0) / 2;
  }

  pickUniqueLabel(baseLabel: string, hasRedundancy = false, usedLabels = this.usedLabels, doNotClean = false) {
    const cleanBaseLabel = doNotClean ?
      `${baseLabel}_` :
      baseLabel.replace(/\d+$/g, '');
    let result;
    let index = 1;
    do {
      result = `${cleanBaseLabel}${index++}`;
      // eslint-disable-next-line no-unmodified-loop-condition
    } while (usedLabels[result] || (hasRedundancy && (usedLabels[`${result}_1`] || usedLabels[`${result}_2`])));
    return result;
  }

  @computed
  get zoneNodes() {
    return groupBy(this.nodes, 'role');
  }

  @computed
  get nodesByName() {
    return groupBy(sortBy(this.nodes, ({name, pairOrder}) => `${name}:${pairOrder}`), 'name');
  }

  @computed
  get rolesCount() {
    return countBy(this.nodes, 'role');
  }

  @computed
  get nodeRailSpeed(): Dictionary<Dictionary<string>> {
    return transform(
      this.linksGroupsSorted,
      (acc, {fromNode, speedString, railIndex}) => {
        if (!isNull(railIndex)) assign(acc[fromNode!.id], {[`${railIndex}`]: speedString});
      },
      {}
    );
  }

  @computed
  get railsGenerics(): Dictionary<string[]> {
    return transform(
      this.linksGroupsSorted,
      (acc, {fromNode, toNode, isInRail, railIndex}) => {
        if (isInRail) {
          (acc[`${toNode!.id}.${railIndex}`] ??= []).push(fromNode!.id);
        }
      },
      {}
    );
  }

  @computed.struct
  get nodeLinksGroupsByPeering(): Dictionary<Record<PeerLinksTypesType, number>> {
    return transform(
      this.linksGroupsSorted,
      (acc, {fromNode, peeringType, count}) => {
        forEach(
          fromNode?.nodesInvolved,
          ({id}) => {
            const peeringTypeString = peeringType ? peeringType : 'null';
            if (!get(acc, [id, peeringTypeString])) set(acc, [id, peeringTypeString], 0);
            acc[id][peeringTypeString] = count + acc[id][peeringTypeString];
          }
        );
      },
      {}
    );
  }

  @computed
  get nodesUsedPorts() {
    // For each of link groups
    const result = transform(
      this.linksGroupsSorted,
      (acc, {
        speed, count, isPeer,
        isAttachedToFirst, isAttachedToSecond, isDualAttached,
        fromRole, toRole, fromNode, toNode
      }) => {
        if (!count || !fromNode || !toNode || !speed) return;

        const fromNodes = isPeer ? [fromNode] : fromNode.nodesInvolved;
        const toNodes = isPeer ? [fromNode.pairedWith] : toNode.nodesInvolved;

        const fromPair = size(fromNodes) === 2;
        const toPair = size(toNodes) === 2;
        const isBetweenPairs = fromPair && toPair;
        const doubleCount = 2 * count;

        // Count taken ports depending on how many nodes on the ends of the link and
        // whether these are single- or double-attached
        const portsTaken = isBetweenPairs ?
          (isDualAttached ?
              [[doubleCount, doubleCount], [doubleCount, doubleCount]] :
              [[count, count], [count, count]]) :
          (fromPair ?
            [[count, count], [doubleCount]] :
            (toPair ?
              [
                [isDualAttached ? doubleCount : count],
                [isAttachedToFirst ? count : 0, isAttachedToSecond ? count : 0]
              ] :
              [[count], [count]]));

        // Collect used ports nodeId/speed/role : count
        forEach([fromNodes, toNodes], (nodes, index) => {
          const counts = portsTaken[index];
          const role = index ? fromRole : toRole;
          forEach(nodes, (node, nodeIndex) => {
            const id = node?.id;
            if (id && role) {
              (acc[id] ??= []).push(...Port.generate(counts[nodeIndex], speed, role));
            }
          });
        });
      },
      {}
    );

    // For L3 Clos Design links per spine setting must be taken into account
    if (this.isL3Clos) {
      forEach(
        // For all LEAF nodes
        this.leafNodes,
        // mark corresponding amount of SPINE ports as taken
        ({id, spineLinksCount, spineLinksSpeed}) => {
          (result[id] ??= []).push(...Port.generate(
            spineLinksCount, speedFromString(spineLinksSpeed), PORT_ROLES.SPINE
          ));
        }
      );
    }

    return result;
  }

  @computed
  get linksGroupsByName(): Dictionary<LinksGroup[]> {
    return transform(
      this.linksGroupsSorted,
      (acc, linkGroup) => {
        (acc[linkGroup.fromName] ??= []).push(linkGroup);
        if (!linkGroup.isPeer) (acc[linkGroup.toName] ??= []).push(linkGroup);
      },
      {}
    );
  }

  @computed
  get railsLinks() {
    return omit(groupBy(this.linksGroups, 'railIndex'), 'null');
  }

  @computed
  get lagLinksCount() {
    return size(filter(this.linksGroups, ({lagMode, railIndex}) => lagMode !== LAG_MODES.NO_LAG && railIndex === null));
  }

  @action
  calculateZonesSizes() {
    forEach(
      this.zones,
      (zone, role) => {
        if (zone.roleBased) {
          zone.maxY = max(
            [ctrlGridStep, ...map(this.zoneNodes[role], 'relativePosition.y')]
          ) + ctrlNodeHeight + ctrlGridStep;
          if (zone.size < zone.maxY) zone.size = zone.maxY;
        }
      }
    );
  }

  makeDraftLinkGroups(fromNodes: Node[], toNodes: Node[]) {
    return transform(fromNodes, (result, sourceNode) => {
      const skipIds = {};

      forEach(toNodes, (destinationNode) => {
        if (skipIds[destinationNode.id]) return; // Avoid links duplication for pairs

        const toPair = destinationNode.isPaired && toNodes.includes(destinationNode.pairedWith!);
        if (toPair) skipIds[destinationNode.pairedWith!.id] = true;

        const linkGroup = LinksGroup.createFilledWith({
          label: makeSafeName(`${sourceNode?.label}_${destinationNode?.label}_${randomPrefix()}`),
          fromName: sourceNode.name,
          toName: destinationNode.name,
          isAttachedToFirst: toPair || destinationNode.isFirstInPair,
          isAttachedToSecond: toPair || !destinationNode.isFirstInPair
        }, this);
        linkGroup.makeDraft();
        result[linkGroup.id] = linkGroup;
      });
    }, {});
  }

  // Initialize the new links group with selected nodes and label
  addDraftLinksGroup(selection: SelectionStore): void {
    const selectedNodes = map(selection.selectedNodesIds, (id) => this.nodes[id]);

    const selectedNames = uniq(map(selectedNodes, 'name'));
    const namesCount = size(selectedNames);
    const isPair = namesCount === 1 && selection.size === 2 && size(this.nodesByName[selectedNames[0]]) === 2;

    if (isPair || namesCount === 2) {
      // Ordinary links ()
      // Links are always oriented from lower zone to the uppers
      const [firstNode, secondNode] = isPair ?
        this.nodesByName[selectedNames[0]] :
        sortBy(
          map(selectedNames, (name) => this.nodesByName[name][0]),
          ({role}) => -ZONES_ROLE_ORDER.indexOf(role)
        );

      const oneToPair = !firstNode.isPaired && secondNode.isPaired;
      const pairToOne = firstNode.isPaired && !secondNode.isPaired;

      const result = LinksGroup.createFilledWith({
        label: makeSafeName(`${firstNode?.label}_${secondNode?.label}_${randomPrefix()}`),
        fromName: firstNode.name,
        toName: secondNode.name,
        isAttachedToFirst:
          // If one to pair and selection contains first node in pair
          (oneToPair && selection.isSelected(secondNode.nodesInvolved[0].id)) ||
          // In all other cases always on
          !oneToPair,
        isAttachedToSecond:
          pairToOne ||
          (oneToPair && selection.isSelected(secondNode.nodesInvolved[1].id))
      }, this);
      result.makeDraft();
      this.addLinksGroup(result);
    } else if (namesCount > 2) {
      // Multiple links creation (Generics to Leafs)
      const nodesByRole = groupBy(selectedNodes, 'role');
      this.linksGroups = {
        ...this.linksGroups,
        ...this.makeDraftLinkGroups(nodesByRole[NODE_ROLES.GENERIC], nodesByRole[NODE_ROLES.LEAF])
      };
    }
  }

  @action
  toggleLinker(linksGroupId?: string, selection?: SelectionStore) {
    this.openedLinksGroupId = undefined;
    if (linksGroupId) {
      // Open specific link for editing
      this.openedLinksGroupId = linksGroupId;
      this.removeDraftLinks();
    } else if (this.isLinkerOpened) {
      // Close opened linker pop-up
      this.removeDraftLinks();
    } else if (selection) {
      // Create new draft link(s)
      this.addDraftLinksGroup(selection);
    }
  }

  @action
  setProperty(property, value: any = null) {
    this[property] = value;
  }

  @action
  toggleOptions(forceHide = false) {
    this.optionsAreShown = forceHide ? false : !this.optionsAreShown;
  }

  @action
  reset(id: string, name: string, description: string, design, devices: any[], tags: string[], supportsRails: boolean) {
    this.id = id;
    this.name = name;
    this.description = description;
    this.design = design;
    this.supportsRails = supportsRails;

    // Removes all existing nodes and links
    this.nodes = {};
    this.linksGroups = {};

    this._tags = [...tags];
    this.editedNode = null;
    this.changes.reset();

    // Precalculate list of ports for every LD
    this.devices = keyBy(
      map(devices, (logicalDevice) => ({
        ...logicalDevice,
        ports: sortBy(
          transform(
            logicalDevice?.panels,
            (acc: Port[], {port_groups: groups}) => {
              forEach(groups, (groupData) => acc.push(...Port.parseGroup(groupData)));
            },
            []
          ),
          'rolesSize'
        )
      })),
      'id'
    );

    let previousZone;
    this.zones = transform(DEFAULT_ZONES, (acc, role) => {
      const zone = new Zone(role, previousZone, this);
      if (role === RAIL_ZONE) zone.makeCustom();
      acc[role] = zone;
      previousZone = zone;
    }, {});
  }

  pickFreePortChannelIdFromRange(minId: number | undefined, maxId: number | undefined,
    existingPortChannelIds: number[]): number {
    if (isNumber(minId) && isNumber(maxId)) {
      let portChannelId = minId;
      while (portChannelId <= maxId) {
        if (!existingPortChannelIds?.includes(portChannelId)) return portChannelId;
        portChannelId++;
      }
    }
    return 0;
  }

  @action
  createNode(nodeData: NodeDataType, role: NODE_ROLES, usedPositions, switchesByName: SwitchesByNameType, preferences) {
    const {label, links, instance_count: instanceCount, count, redundancy_protocol: redundancy,
      port_channel_id_min: pcIdMin, port_channel_id_max: pcIdMax} = nodeData;

    const isLeaf = role === NODE_ROLES.LEAF;
    const isGeneric = role === NODE_ROLES.GENERIC;

    const hasRedundancy = !!redundancy && redundancy !== NO_REDUNDANCY;
    const instances = instanceCount || count || 1;
    const multipleInstances = instances > 1;

    const switches = switchesByName[first(links)?.target_switch_label];
    const firstSwitch = first(switches);
    const isConnectedToAccess = firstSwitch?.isAccessSwitch;
    const isGenericConnectedToAccess = isGeneric && isConnectedToAccess;

    let node: Node;
    const nodePreferences = preferences?.[label];

    const genericsToGetPcId: [Node, number, number][] = [];

    forEach(
      // If generic is connected to an access - amount of nodes depends on
      // amount of switches
      isGenericConnectedToAccess ? switches : [firstSwitch],
      (switchNode) => {
        const switchPosition = switchNode?.relativePosition;

        // Wnen too many generics get created, they might be distributed evenly
        // in 4 columns
        const verticalLimit = instances > 7 ? instances / 4 : instances;
        let index = 0;
        let _x = switchPosition?.x ?? 0;

        forEach(range(0, instances), () => {
          const id = generateLocalId();
          let x, y;
          if (!multipleInstances && nodePreferences) {
            x = nodePreferences?.x ?? 0;
            y = nodePreferences?.y ?? 0;
          } else if (switchPosition && isGeneric) {
            // Generic nodes must be placed under their switches.
            // They also might be positioned diagonally to reduce links intersection.
            x = _x + index++ * ctrlGridStep * 0;
            const _y = +usedPositions[_x] || 0;
            y = ctrlGridStep + _y * (ctrlNodeHeight + ctrlGridStep);
            usedPositions[_x] = _y + 1;
            if (index === verticalLimit) {
              index = 0;
              _x += ctrlNodeWidth + ctrlGridStep;
            }
          } else {
            // Switches are placed horizontally by default
            x = 400 + (ctrlNodeWidth + ctrlGridStep) * usedPositions['']++;
            y = ctrlGridStep;
          }

          if (!node || !isGeneric || !switchNode?.pairOrder) {
            const uniqueLabel = multipleInstances ?
              this.pickUniqueLabel(label, hasRedundancy, switchesByName, true) :
              label;

            // A single generic node must be created for the switch pair, so
            // using the same node for the second peer
            node = Node.deserialize({...nodeData, 'node-type': ROLES_NODE_TYPES[role]}, this);

            node.fillWith({
              id,
              name: id,
              label: uniqueLabel,
              relativePosition: {x, y}
            });

            this.nodes = {...this.nodes, [node.id]: node};

            switchesByName[uniqueLabel] = [];
            (switchesByName[label] ??= []).push(node);
            if (hasRedundancy) {
              switchesByName[`${uniqueLabel}_1`] = [];
              switchesByName[`${uniqueLabel}_2`] = [];
            }

            if (isGeneric) {
              if (node.portChannelId > 0) {
                // If PCID range is defined - collect all nodes to pick PCID from the range when all links are created
                genericsToGetPcId.push([node, node.portChannelId, node.portChannelIdExt]);

                // For generics PCID must be generated from the given range
                node.fillWith({
                  portChannelId: 0,
                  portChannelIdExt: 0
                });
              }

              // Access switches generate generics, leafs - don't
              const serverName = this.createLinksGroups(
                node,
                links,
                isGenericConnectedToAccess ?
                  {[links[0].target_switch_label]: [switchNode]} as SwitchesByNameType :
                  switchesByName
              );

              if (serverName) {
                // Pick generic's PCID from the given range
                node.portChannelId = this.pickFreePortChannelIdFromRange(
                  pcIdMin, pcIdMax, this.switchPortChannelIds[serverName]
                );
                node.portChannelIdExt = node.portChannelId;
              }
            }
          }

          if (hasRedundancy) {
            this.createPeerNodeFor(node, redundancy);
            if (isLeaf) {
              this.createLeafPeerLinks(node, nodeData as LeafNodeDataType);
            } else {
              this.createAccessPeerLinks(node, nodeData as AccessNodeDataType);
            }
            usedPositions['']++;
          }

          if (!isGeneric) this.createLinksGroups(node, links, switchesByName);
        });
      }
    );

    // Distribute PCIDs for generics
    forEach(
      sortBy(genericsToGetPcId, ([{portChannelIdsNeeded}]) => -portChannelIdsNeeded),
      ([node, rangeMin, rangeMax]) => pickPortChannelFromRange(node, rangeMin, rangeMax)
    );
  }

  @action
  createLeafPeerLinks(node: Node, nodeData: LeafNodeDataType) {
    const {
      redundancy_protocol: redundancy,
      leaf_leaf_l3_link_count: l3Count,
      leaf_leaf_l3_link_speed: l3Speed,
      leaf_leaf_l3_link_port_channel_id: portChannelL3,
      leaf_leaf_link_port_channel_id: portChannelL2,
      leaf_leaf_link_count: count,
      leaf_leaf_link_speed: speed
    } = nodeData;

    if (redundancy === REDUNDANCY.MLAG) {
      this.addLinksGroup(LinksGroup.createPeerLinks(node.name, l3Speed, l3Count, this, PEER_LINKS_TYPES.L3_PEER));
      this.addLinksGroup(LinksGroup.createPeerLinks(node.name, speed, count, this, PEER_LINKS_TYPES.L2_PEER));
      node.setProperty('portChannelId', portChannelL2, true);
      node.setProperty('portChannelIdExt', portChannelL3, true);
    }
  }

  @action
  createAccessPeerLinks(node: Node, nodeData: AccessNodeDataType) {
    const {
      redundancy_protocol: redundancy,
      access_access_link_count: count,
      access_access_link_port_channel_id_min: portChannelMin,
      access_access_link_port_channel_id_max: portChannelMax,
      access_access_link_speed: speed
    } = nodeData;
    if (redundancy === REDUNDANCY.ESI) {
      this.addLinksGroup(LinksGroup.createPeerLinks(node.name, speed, count, this, PEER_LINKS_TYPES.L3_PEER));

      // Pick available PCID for access switches' peer links
      node.setProperty(
        'portChannelId',
        this.pickFreePortChannelIdFromRange(portChannelMin, portChannelMax, this.switchPortChannelIds[node.name]),
        true
      );
      node.setProperty('portChannelIdExt', node.portChannelId, true);
    }
  }

  @action
  createPeerNodeFor(node, redundancy = REDUNDANCY.NO_LAG, pair = node.clone()) {
    // Switch pair creation
    pair.fillWith({
      _label: node._label,
      name: node.name,
      pairedWith: node,
      relativePosition: {x: node.relativePosition.x + ctrlNodeWidth + ctrlGridStep, y: node.relativePosition.y}
    });
    node.isFirstInPair = true;
    this.nodes = {...this.nodes, ...{[pair.id]: pair}};
    node.pairedWith = pair;
    node.redundancyProtocol = redundancy;
    return pair;
  }

  @action
  createLinksGroups(fromNode: Node, links: any[], switchesByName: SwitchesByNameType): string | undefined {
    // Return if no links data
    if (!links) return;

    let firstSwitchName;
    // A single generic could either be connected to an access switch OR
    // to [different] leafs:
    forEach(links, (jsonValue) => {
      // Create links group
      const linksGroup = LinksGroup.deserialize({...jsonValue, node_label: fromNode.name}, this);
      const firstSwitch = first(switchesByName?.[linksGroup.toName]);
      if (firstSwitch) {
        linksGroup.fillWith({toName: firstSwitch.name});
        this.addLinksGroup(linksGroup);
        firstSwitchName = firstSwitch.name;
      }
    });
    return firstSwitchName;
  }

  @action
  parseNodes(data, role, switchesByName: SwitchesByNameType, preferences) {
    const usedPositions = {'': 0};
    forEach(data, (nodeData) => this.createNode(nodeData, role, usedPositions, switchesByName, preferences));

    this.calculateZonesSizes();
    this.updateArea();
  }

  @action
  updateArea() {
    this.positioner.update(values(this.nodes));
  }

  @action
  addNode(role) {
    // New node is added to generics zone by default
    const zone = this.zones[role];
    const node = new Node({
      id: generateLocalId(),
      label: this.pickUniqueLabel(role),
      zone,
      role: zone.role,
      relativePosition: {
        x: 200 + Math.random() * 100,
        y: ctrlGridStep + Math.random() * (min([zone?.size ?? 0, 200]) as number)
      },
      _order: size(this.nodes) + 1,
    }, this);
    this.nodes = {...this.nodes, ...{[node.id]: node}};
    this.updateArea();

    this.changes.add(node);
    return node;
  }

  // Updates the item and returns corresponding Step
  _changeItem(item, update) {
    const step = Step.modification(item).inverted;
    item.fillWith(update);
    step.setResult(item);
    return step;
  }

  @action
  deleteNode(id: string, batchChange?: Change) {
    const node = this.nodes[id];
    let affectedNodesIds: string[] = [];

    // Collect all changes into one batch
    const change = batchChange || [];

    const linksAffected = filter(this.linksGroups, (linksGroup) => linksGroup.contains([node]));

    let linksIdsToBeRemoved: string[] = [];
    if (node.isPaired) {
      // Add pair for revalidation
      affectedNodesIds.push(node.pairedWith!.id);

      // If node is paired, some of pairs existing links
      // must be validated (whether the remaining pair node
      // is capable of adopting them). Links must be removed if not.
      linksIdsToBeRemoved = transform(
        linksAffected,
        (acc: string[], linksGroup) => {
          const {id, isPeer, isDualAttached, isBetweenPairs} = linksGroup;
          // All peer links are always deleted
          if (isPeer) {
            // Peer links must be deleted
            acc.push(id);
            change.push(Step.deletion(linksGroup));
          } else if (isBetweenPairs) {
            // Single-homed links between switch pairs must be modified
            if (!isDualAttached) {
              change.push(this._changeItem(linksGroup, {
                isAttachedToFirst: !node.isFirstInPair,
                isAttachedToSecond: node.isFirstInPair
              }));
            }
          } else {
            // Links from pair to switch
            // if dual-attached - remove node's attachment
            change.push(this._changeItem(linksGroup, {
              isAttachedToFirst: true,
              isAttachedToSecond: false
            }));
          }
        },
        []
      );

      // The reference to the node must be removed from the paired one along with all other
      // pairing properties
      change.push(this._changeItem(
        node.pairedWith,
        {
          isFirstInPair: false,
          pairedWith: null,
          portChannelId: 0,
          portChannelIdExt: 0,
          redundancyProtocol: null
        })
      );
    } else {
      // Otherwise just remove all the links given node is a part of.
      linksIdsToBeRemoved = map(linksAffected, 'id');

      // Add links removal to the change
      forEach(linksAffected, (linksGroup) => {
        change.push(Step.deletion(linksGroup));
      });

      affectedNodesIds = without(
        uniq(
          transform(linksAffected, (acc: string[], {fromName, toName}) => {
            acc.push(...map(this.nodesByName[fromName], 'id'));
            acc.push(...map(this.nodesByName[toName], 'id'));
          }, [])
        ),
        id
      );
    }
    // Remove related links if any
    if (size(linksIdsToBeRemoved)) {
      this.linksGroups = omit(this.linksGroups, linksIdsToBeRemoved);
    }

    // Track node deletion
    change.push(Step.deletion(node));

    // Rebuild nodes collection
    this.nodes = omit(this.nodes, id);

    // Update the area
    this.updateArea();

    if (!batchChange) this.changes.register(change);
  }

  @action
  editNode(node?: Node) {
    // Toggles node properties popup
    this.editedNode = (node && node?.id !== this.editedNode?.id) ? node : null;
  }

  // Checks if two nodes can have a link between them based on their roles and existing links
  getLinkingRoleRestrictionFor(sourceNode: Node | null, destinationNode: Node | null) {
    // If one of the nodes is not provided - no restrictions
    if (!sourceNode || !destinationNode) return;

    const firstLink = find(sourceNode?.myLinksGroups, {
      isPeer: false,
      isDraft: false,
      fromName: sourceNode.name
    });

    const isPeer = sourceNode === destinationNode || sourceNode.isPairedWith(destinationNode.id);

    if (sourceNode.isGeneric) {
      if (destinationNode.isGeneric) {
        return 'Generic systems cannot be linked';
      }
      if (firstLink) {
        if (firstLink.toRole !== destinationNode.role) {
          return 'Generic system cannot be linked to Leaf and Access switch at the same time';
        } else if (destinationNode.isAccessSwitch && destinationNode.name !== firstLink.toName) {
          return 'Generic system cannot be linked with different Access switches';
        }
      } else {
        // Generic has no links - it can be connected to any switch with
        // sufficient ports/roles
        return;
      }
    } else if (!isPeer) {
      if (sourceNode.isPaired && destinationNode.isMlagLeafPair) {
        // Forbidden to create links between pairs with different redundancy
        return 'Unable to create links between switch pairs with different redundancy protocols (ESI ↔ MLAG)';
      } else if (firstLink) {
        // Source node is Access switch with existing link to Leaf
        return 'Access switch can only have one logical link to Leaf';
      }
    }
  }

  @action
  cloneSelected(selectedNodesIds: string[], withoutLinks = false, registerChanges = true) {
    // Clones selected nodes on the canvas
    const selectedNodes = map(selectedNodesIds, (nodeId) => this.nodes[nodeId]);
    const batchChange: Step[] = [];

    // Generic nodes' PCIDs must be re-calculated only after their links are cloned
    const clonedGenerics: [Node, number, number][] = [];

    // Clone nodes
    const newNodesIds: string[] = [];
    const nodeNamesMap = transform(
      selectedNodes,
      (acc, node) => {
        const newNode = node.clone();
        newNodesIds.push(newNode.id);

        // Cloned node positioning: single generic is always cloned below its origin,
        // everything else gets cloned one position right
        if (newNode.isGeneric && size(selectedNodesIds) === 1) {
          // Generic get cloned below the origin
          newNode.moveBy({x: 0, y: ctrlNodeHeight + ctrlGridStep});
        } else {
          // ... while switches - on the right
          newNode.moveBy({x: ctrlNodeWidth + ctrlGridStep, y: 0});
        }

        // Check if cloned node is a switch-pair
        if (node.isPaired) {
          // Check if cloned yet
          const clonedPair = first(this.nodesByName?.[acc[node.pairedWith!.name]]);
          if (clonedPair) {
            // ... and pair clones too
            newNode.pairWith(clonedPair);
            newNode.isFirstInPair = node.isFirstInPair;
            clonedPair.isFirstInPair = !node.isFirstInPair;
            clonedPair.redundancyProtocol = node.redundancyProtocol;
            newNode.name = clonedPair.name;
            newNode._label = clonedPair._label;

            // Cloned pair might not be a part of cloning set - so, redundancy protocol
            // and pairing must only be set if both part of a pair are being cloned
            const cloneStep = find(batchChange, {id: clonedPair.id});
            cloneStep?.setResult(clonedPair);
          }
        }

        acc[node.name] = newNode.name;

        this.nodes = {...this.nodes, ...{[newNode.id]: newNode}};

        // If source is generic with PCID > 0, clone must obtain the new PCID after its links are cloned
        if (node.isGeneric && node.portChannelId > 0) {
          // Clone's PCID must be reset to 0 not to interfere with the existing ones
          newNode.fillWith({portChannelId: 0, portChannelIdExt: 0});
          clonedGenerics.push([newNode, node.portChannelId, node.portChannelIdExt]);
          // These nodes will be added to the changeset after PCIDs are calculated
        } else {
          // Add node to the changeset otherwise
          batchChange.push(Step.creation(newNode));
        }
      },
      {}
    );

    // Links should not be cloned for split switch pairs
    const nodesWithoutSplitPairs = filter<Node>(selectedNodes, ({isPaired, pairedWith}) => (
      !isPaired || selectedNodesIds.includes(pairedWith!.id)
    ));

    if (!withoutLinks) {
      const railIndicesMap = {};

      // Clone links groups
      forEach(
        filter(
          values(this.linksGroups),
          (link) => link.contains(nodesWithoutSplitPairs)
        ),
        (linksGroup) => {
          // Checking if linksGroup cloning is technically possible
          const [sourceNode, destinationNode] = [
            first(this.nodesByName[nodeNamesMap?.[linksGroup.fromName] ?? linksGroup.fromName]) || null,
            first(this.nodesByName[nodeNamesMap?.[linksGroup.toName] ?? linksGroup.toName]) || null,
          ];
          const restriction = this.getLinkingRoleRestrictionFor(sourceNode, destinationNode);
          if (!restriction) {
            const clone = linksGroup.cloneFor(nodeNamesMap);
            clone.makeLabelUnique();
            if (clone.isInRail) {
              const isLeafCloned = !!nodeNamesMap[linksGroup.toName];
              if (isLeafCloned) {
                // If leaf of the rail is cloned - all rail indices must be regenerated too
                const index = isNull(clone.railIndex) ? 'null' : clone.railIndex;
                const newIndex = isNil(railIndicesMap[index]) ?
                  this.availableRailIndex : railIndicesMap[index];
                railIndicesMap[index] = newIndex;
                clone.railIndex = newIndex;
              }
            }

            batchChange.push(Step.creation(clone));
            this.addLinksGroup(clone);
          }
        }
      );
    }

    // Re-calculate PCIDs for cloned generics
    forEach(clonedGenerics, ([generic, minRange, maxRange]) => {
      // If source had PCID - pick a new one from the range [minRange, maxRange + 100]
      pickPortChannelFromRange(generic, minRange, maxRange + 100);
      // Adding updated generic to the changeset
      batchChange.push(Step.creation(generic));
    });

    this.updateArea();
    if (registerChanges) this.changes.register(batchChange);

    return newNodesIds;
  }

  @action
  deleteNodes(selectedNodesIds: string[]) {
    const batchChange = [];
    forEach(selectedNodesIds, (id) => {
      this.deleteNode(id, batchChange);
    });
    this.updateArea();
    this.changes.register(batchChange);
  }

  @action
  applyChange(change: Step[]) {
    // Applies change from the history (undo/redo operations)
    if (!change?.length) return [];

    // For each step in the change
    const affectedNodesIds: string[] = transform(change, (acc: string[], step: Step) => {
      if (!step.type) return;

      const collectionName = collections[step.type];
      const collection = this[collectionName];

      // Preserving the target item
      let item: Node | LinksGroup = collection?.[step.id] ?? {};
      if (step.isDelete) {
        this[collectionName] = omit(collection, step.id!);
      } else {
        item = (entities[step.type]).deserialize(step.item, this);
        // Update the item + rebuild the collection
        this[collectionName] = {...collection, [item.id]: item};
        if (step.isNode) {
          acc.push(item.id);
        }
      }
    }, []);
    this.updateArea();
    // Returns Ids of affected nodes for selection
    return affectedNodesIds;
  }

  @action
  addLinksGroup(linksGroup: LinksGroup) {
    this.linksGroups = {...this.linksGroups, ...{[linksGroup.id]: linksGroup}};
  }

  @action
  commitLinksGroup = () => { // Keeping the context of RackStore
    if (this.hasDraftLinks) {
      this.changes.register(map(this.draftLinksGroups, (newLinkGroup) => Step.creation(newLinkGroup)));
      this.commitDraftLinks();
    }
  };

  @action
  deleteLinksGroups(ids: string[]) {
    if (ids.includes(this.openedLinksGroupId!)) this.openedLinksGroupId = undefined;
    this.linksGroups = omit(this.linksGroups, ids);
  }

  canLinkNodes(nodes, linksGroup, forcedCount) {
    return every(nodes, (node) => node.canConnectTo(linksGroup, forcedCount));
  }

  @action
  undo() {
    return this.applyChange(this.changes.undo() as Step[]);
  }

  @action
  redo() {
    return this.applyChange(this.changes.redo() as Step[]);
  }

  @computed
  get isValid() {
    return this.name && every(this.nodes, 'isValid');
  }

  constructor(rootStore) {
    makeObservable(this);
    this.rootStore = rootStore;

    this.changes = new ChangesStore(rootStore);
  }

  makePayload() {
    return toJS({
      id: this.id,
      display_name: this.name,
      description: this.description,
      fabric_connectivity_design: this.design,
      tags: this._serializeTags(),
      leafs: this._serializeLeafs(),
      access_switches: this._serializeAccessSwitches(),
      generic_systems: this._serializeGenerics(),
      logical_devices: this._serializeLogicalDevices(),
      servers: [],
      preferences: this._gatherPreferences()
    });
  }

  _serializeTags() {
    return map(this.usedTags, (label) => ({label, description: ''}));
  }

  _serializeLeafs() {
    return map(
      filter(this.nodes, {isLeaf: true, isFirstInPair: false}),
      (node) => {
        const peerLinks = groupBy(
          filter(
            this.linksGroupsByName[node.name],
            {isPeer: true}
          ),
          'peeringType'
        );

        const l2Peers = first(peerLinks?.[PEER_LINKS_TYPES.L2_PEER]) || noLinks;
        const l3Peers = first(peerLinks?.[PEER_LINKS_TYPES.L3_PEER]) || noLinks;

        return toJS({
          ...node.serialize(false),

          leaf_leaf_link_count: l2Peers.count,
          leaf_leaf_link_speed: l2Peers.count ? l2Peers.speed : null,

          leaf_leaf_l3_link_count: l3Peers.count,
          leaf_leaf_l3_link_speed: l3Peers.count ? l3Peers.speed : null
        });
      }
    );
  }

  _serializeAccessSwitches() {
    return map(
      filter(this.nodes, {isAccessSwitch: true, isFirstInPair: false}),
      (node) => {
        const peerLinks = groupBy(
          filter(
            this.linksGroupsByName[node.name],
            {isPeer: true}
          ),
          'peeringType'
        );

        const l3Peers = first(peerLinks?.[PEER_LINKS_TYPES.L3_PEER]) || noLinks;

        return toJS({
          ...node.serialize(false),
          access_access_link_count: l3Peers.count,
          access_access_link_speed: l3Peers.count ? l3Peers.speed : null,
          instance_count: 1,

          links: this._serializeLinks(node.name)
        });
      }
    );
  }

  _serializeGenerics() {
    return map(
      filter(this.nodes, {isGeneric: true}),
      (node) => {
        return toJS({
          ...node.serialize(false),
          loopback: 'disabled',
          asn_domain: 'disabled',
          management_level: 'unmanaged',
          count: 1,

          links: this._serializeLinks(node.name)
        });
      }
    );
  }

  _serializeLinks(fromName) {
    const links = filter(
      this.linksGroupsByName[fromName],
      {fromName, isPeer: false}
    );

    return map(links, (linkGroup) => linkGroup.serialize(false));
  }

  _serializeLogicalDevices() {
    return map(
      compact(uniq(map(this.nodes, ({logicalDevice}) => logicalDevice?.id))),
      (logicalDeviceId) => toJS(omit(this.devices[logicalDeviceId], 'custom'))
    );
  }

  _gatherPreferences() {
    return {
      positions: transform(
        this.nodes,
        (acc: NodePositionType[], {_label, isPaired, isFirstInPair, relativePosition: {x, y}}) => {
          if (!isPaired || isFirstInPair) acc.push({label: _label, x, y});
        },
        [])
    };
  }

  @action
  setError(header?, content?, asJson?) {
    this.setProperty(
      'error',
      header ?
        <>
          <Message.Header>{header}</Message.Header>
          <Message.Content>{asJson ?
            <pre>{JSON.stringify(content, null, 2)}</pre> :
            content}
          </Message.Content>
        </> :
        null
    );
  }

  @action
  async submit(onClose, httpMethod: string, httpUrl: string) {
    this.isSaving = true;
    this.setError();
    try {
      // eslint-disable-next-line
      await request(
        httpUrl,
        {
          method: httpMethod,
          body: JSON.stringify(this.makePayload())
        }
      );
      onClose();
    } catch (e: any) {
      this.setError(
        'Server error',
        e.responseBody?.errors ?? e.responseBody,
        true
      );
      this.isSaving = false;
    }
  }
}

export default RackStore;
